import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../App';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Menu, X, User, LogOut, Home, Building, HelpCircle, Phone, ChevronDown, Plus } from 'lucide-react';
import LanguageSelector from './LanguageSelector';

const Header = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getDashboardLink = () => {
    if (!user) return '/login';
    switch (user.user_type) {
      case 'buyer': return '/buyer';
      case 'agent': return '/agent';
      case 'seller': return '/seller';
      case 'admin': return '/admin';
      default: return '/';
    }
  };

  const navLinks = [
    { href: '/', label: 'Home' },
    { href: '/immobili', label: 'Immobili' },
    { href: '/come-funziona', label: 'Come Funziona' },
    { href: '/servizi-ai', label: 'Servizi AI' },
    { href: '/contatti', label: 'Contatti' },
  ];

  return (
    <header className="glass-header sticky top-0 z-50" data-testid="main-header">
      <div className="container-apple">
        <div className="flex items-center justify-between h-14 lg:h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2" data-testid="header-logo">
            <img 
              src="/weqasa-logo.png" 
              alt="WEQASA" 
              className="h-10 sm:h-12 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation - Centered */}
          <nav className="hidden lg:flex items-center space-x-8 absolute left-1/2 transform -translate-x-1/2">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className="text-[14px] text-[#1D1D1F] hover:text-[#86868B] transition-colors font-medium"
                data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right Section */}
          <div className="hidden lg:flex items-center space-x-4">
            <LanguageSelector />
            
            {!isAuthenticated ? (
              <>
                <Link 
                  to="/login"
                  className="text-[14px] text-[#1D1D1F] hover:text-[#86868B] transition-colors font-medium"
                  data-testid="header-login"
                >
                  Accedi
                </Link>
                <button 
                  className="inline-flex items-center px-5 py-2.5 text-[14px] font-semibold text-white bg-[#0071e3] hover:bg-transparent hover:text-[#0071e3] border-2 border-[#0071e3] rounded-full transition-all"
                  onClick={() => navigate('/registrati/proprietario')}
                  data-testid="header-seller-cta"
                >
                  <Plus className="h-4 w-4 mr-1.5" strokeWidth={2} />
                  Inserisci Immobile
                </button>
              </>
            ) : (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button 
                    className="flex items-center space-x-2 px-4 py-2 rounded-full bg-[#F5F5F7] hover:bg-[#E8E8ED] transition-colors"
                    data-testid="user-menu-trigger"
                  >
                    <div className="w-7 h-7 bg-[#1D1D1F] rounded-full flex items-center justify-center">
                      <User className="h-4 w-4 text-white" strokeWidth={1.5} />
                    </div>
                    <span className="text-[14px] font-medium text-[#1D1D1F]">
                      {user?.first_name || 'Account'}
                    </span>
                    <ChevronDown className="h-4 w-4 text-[#86868B]" strokeWidth={1.5} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent 
                  align="end" 
                  className="w-56 bg-white/90 backdrop-blur-xl border border-black/5 rounded-2xl shadow-[0_10px_40px_rgb(0,0,0,0.08)] p-2"
                >
                  <div className="px-3 py-2 mb-1">
                    <p className="text-[14px] font-semibold text-[#1D1D1F]">
                      {user?.first_name} {user?.last_name}
                    </p>
                    <p className="text-[12px] text-[#86868B]">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator className="bg-black/5" />
                  <DropdownMenuItem 
                    onClick={() => navigate(getDashboardLink())}
                    className="rounded-xl cursor-pointer text-[14px] text-[#1D1D1F] focus:bg-[#F5F5F7]"
                  >
                    <Building className="mr-2 h-4 w-4" strokeWidth={1.5} />
                    Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-black/5" />
                  <DropdownMenuItem 
                    onClick={handleLogout}
                    className="rounded-xl cursor-pointer text-[14px] text-red-600 focus:bg-red-50"
                  >
                    <LogOut className="mr-2 h-4 w-4" strokeWidth={1.5} />
                    Esci
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 rounded-xl hover:bg-[#F5F5F7] transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? (
              <X className="h-6 w-6 text-[#1D1D1F]" strokeWidth={1.5} />
            ) : (
              <Menu className="h-6 w-6 text-[#1D1D1F]" strokeWidth={1.5} />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white/95 backdrop-blur-xl border-t border-black/5">
          <div className="container-apple py-6 space-y-6">
            <nav className="space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-4 py-3 text-[17px] font-medium text-[#1D1D1F] hover:bg-[#F5F5F7] rounded-xl transition-colors"
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            <div className="border-t border-black/5 pt-6 space-y-3">
              {!isAuthenticated ? (
                <>
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      navigate('/registrati/proprietario');
                    }}
                    className="w-full py-3.5 text-[17px] font-semibold text-white bg-[#0071e3] hover:bg-transparent hover:text-[#0071e3] border-2 border-[#0071e3] rounded-full transition-all"
                  >
                    Inserisci Immobile
                  </button>
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      navigate('/login');
                    }}
                    className="w-full py-3.5 text-[17px] font-medium text-[#0071e3] bg-transparent hover:bg-[#0071e3] hover:text-white border-2 border-[#0071e3] rounded-full transition-all"
                  >
                    Accedi
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      navigate(getDashboardLink());
                    }}
                    className="w-full py-3.5 text-[17px] font-medium text-[#1D1D1F] bg-[#F5F5F7] hover:bg-[#E8E8ED] rounded-full transition-colors"
                  >
                    Dashboard
                  </button>
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      handleLogout();
                    }}
                    className="w-full py-3.5 text-[17px] font-medium text-red-600 hover:bg-red-50 rounded-full transition-colors"
                  >
                    Esci
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
